import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static values = {
    dancerId: String,
    isAdmin: { type: Boolean, default: false }
  }

  connect() {
    loadTranslations()
    this._load()
  }

  async _load() {
    try {
      const json = await apiGet(`/api/v1/search/dancer/${this.dancerIdValue}`)
      this.dancer = json.data
      this._render()
    } catch (error) {
      console.error("Error loading dancer profile:", error)
      this.element.innerHTML = `<div class="alert alert-danger">${escapeHtml(error.message)}</div>`
    }
  }

  _render() {
    const d = this.dancer
    const avatarHtml = this._avatarHtml(d)
    const classesHtml = this._classesHtml(d.dance_classes)
    const skillsHtml = this._skillsHtml(d.dance_skills)
    const membershipsHtml = this._membershipsHtml(d.memberships)
    const noSubClubs = d.clubs_without_subscription || []

    this.element.querySelector(".col-lg-8").innerHTML = `
      <div class="card shadow-sm mb-4"><div class="card-body text-center py-4">
        <div class="mb-3">${avatarHtml}</div>
        <h4 class="mb-1">${escapeHtml(d.display_name)}</h4>
        ${d.city ? `<p class="text-muted mb-2"><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(d.city)}</p>` : ""}
        ${d.age ? `<p class="text-muted mb-2">${d.age} ${escapeHtml(t("dancer_profile.years_old"))}</p>` : ""}
        ${d.federation_metadata?.age_category ? `<p class="text-muted mb-2"><i class="fa-solid fa-users me-1"></i>${escapeHtml(d.federation_metadata.age_category)}</p>` : ""}
        ${classesHtml ? `<div class="d-flex justify-content-center gap-2 mb-2">${classesHtml}</div>` : ""}
      </div></div>

      ${skillsHtml}
      ${membershipsHtml}

      ${noSubClubs.length > 0 ? `<div class="alert alert-light border shadow-sm p-4 mb-4">
        <div class="d-flex align-items-start gap-3">
          <i class="fa-solid fa-lightbulb fa-lg text-warning mt-1"></i>
          <div>
            <p class="mb-2 fw-medium">${escapeHtml(t("dancer_profile.club_hint_title"))}</p>
            <p class="mb-0 text-muted small">${escapeHtml(t("dancer_profile.club_hint_body"))}</p>
          </div>
        </div>
      </div>` : ""}

      <div class="alert alert-info border-0 shadow-sm p-4 mb-4">
        <div class="d-flex align-items-start gap-3">
          <i class="fa-solid fa-bullhorn fa-lg text-info mt-1"></i>
          <div>
            <p class="mb-2 fw-medium">${escapeHtml(t("dancer_profile.announce_title"))}</p>
            <p class="mb-0 text-muted small">${escapeHtml(t("dancer_profile.announce_body"))}</p>
          </div>
        </div>
      </div>

      <div class="text-center d-flex flex-wrap gap-2 justify-content-center align-items-center">
        <a href="/search" class="default-btn"><span><i class="fa-solid fa-arrow-left me-2"></i>${escapeHtml(t("clubs_search.show.back_to_search", { default: "Wróć do wyszukiwania" }))}</span></a>
        ${this.isAdminValue ? `
          <a href="/management/users/${d.id}/edit" class="default-btn"><span><i class="fa-solid fa-pen me-2"></i>${escapeHtml(t("common.edit", { default: "Edytuj" }))}</span></a>
          <button type="button" class="btn btn-danger dancer-profile-delete-btn" data-user-id="${escapeHtml(d.id)}">
            <i class="fa-solid fa-trash me-2"></i>${escapeHtml(t("common.delete", { default: "Usuń" }))}
          </button>
        ` : ""}
      </div>`

    this.element.querySelectorAll('.dancer-profile-delete-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const form = document.getElementById('dancer-profile-delete-form')
        const modalEl = document.getElementById('dancerProfileDeleteModal')
        if (form && modalEl && btn.dataset.userId) {
          form.action = `/management/users/${btn.dataset.userId}?redirect_to=search`
          const Bootstrap = window.bootstrap
          if (Bootstrap?.Modal) {
            const modal = (typeof Bootstrap.Modal.getInstance === 'function' && Bootstrap.Modal.getInstance(modalEl)) || new Bootstrap.Modal(modalEl)
            modal.show()
          }
        }
      })
    })
  }

  _avatarHtml(d) {
    if (d.avatar_url) {
      return `<img src="${escapeHtml(d.avatar_url)}" class="rounded-circle" style="width: 80px; height: 80px; object-fit: cover;" alt="${escapeHtml(d.display_name)}">`
    }
    const isMale = d.sex === 1
    const icon = isMale ? "fa-mars text-primary" : "fa-venus text-danger"
    return `<div class="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style="width: 80px; height: 80px;"><i class="fa-solid fa-2x ${icon}"></i></div>`
  }

  _classesHtml(classes) {
    if (!classes) return ""
    let html = ""
    if (classes.st) html += `<span class="badge bg-dm-navy">ST: ${escapeHtml(classes.st)}</span>`
    if (classes.la) html += `<span class="badge bg-dm-navy">LA: ${escapeHtml(classes.la)}</span>`
    return html
  }

  _skillsHtml(skills) {
    if (!skills || skills.length === 0) return ""
    const rows = skills.map(s => {
      const levelStr = s.level ? ` <span class="text-muted small">(${escapeHtml(s.level)})</span>` : ""
      return `<div class="col-sm-6"><div class="d-flex align-items-center gap-2 p-2 bg-light rounded">
        <i class="fa-solid fa-circle-check text-success"></i>
        <div><span class="fw-medium">${escapeHtml(s.name)}</span>${levelStr}</div>
      </div></div>`
    }).join("")

    return `<div class="card shadow-sm mb-4"><div class="card-body">
      <h5 class="card-title mb-3"><i class="fa-solid fa-music me-2"></i>${escapeHtml(t("dancer_profile.dance_styles"))}</h5>
      <div class="row g-2">${rows}</div>
    </div></div>`
  }

  _membershipsHtml(memberships) {
    if (!memberships || memberships.length === 0) return ""
    const rows = memberships.map(m => {
      let badges = ""
      if (m.has_subscription) {
        badges += `<a href="/clubs/${escapeHtml(m.club_slug)}" class="text-decoration-none"><span class="badge bg-success">${escapeHtml(m.club_name)}</span></a>`
      } else {
        badges += `<span class="text-muted small">${escapeHtml(t("dancer_profile.club_no_subscription"))}</span>`
      }
      m.federations.forEach(f => {
        badges += `<span class="badge bg-secondary">${escapeHtml(f)}</span>`
      })
      return `<div class="d-flex align-items-center gap-2 flex-wrap mb-3">${badges}</div>`
    }).join("")

    return `<div class="card shadow-sm mb-4"><div class="card-body">
      <h5 class="card-title mb-3"><i class="fa-solid fa-building me-2"></i>${escapeHtml(t("dancer_profile.affiliations"))}</h5>
      ${rows}
    </div></div>`
  }
}
